import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './popover.css';

const VIEWPORT_PADDING = 8;
const POPOVER_GAP = 8;

export default function Popover({
  className,
  children,
  content,
  position = 'bottom',
  anchorRef,
  onClose, // A callback to close the popover
  style,
  ...props
}) {
  const popoverRef = useRef(null);
  const markerRef = useRef(null);
  const [placement, setPlacement] = useState(position);
  const [popoverStyle, setPopoverStyle] = useState(null);

  const updatePosition = useCallback(() => {
    const popover = popoverRef.current;
    const anchor =
      anchorRef?.current ?? markerRef.current?.previousElementSibling;

    if (!popover || !anchor) return;

    const anchorRect = anchor.getBoundingClientRect();
    const popoverRect = popover.getBoundingClientRect();
    const spaceAbove = anchorRect.top - VIEWPORT_PADDING;
    const spaceBelow = window.innerHeight - anchorRect.bottom - VIEWPORT_PADDING;
    const preferredPlacement =
      position === 'top' || position === 'bottom' ? position : 'bottom';
    const nextPlacement =
      preferredPlacement === 'top'
        ? spaceAbove >= popoverRect.height || spaceAbove >= spaceBelow
          ? 'top'
          : 'bottom'
        : spaceBelow >= popoverRect.height || spaceBelow >= spaceAbove
          ? 'bottom'
          : 'top';

    const idealTop =
      nextPlacement === 'top'
        ? anchorRect.top - popoverRect.height - POPOVER_GAP
        : anchorRect.bottom + POPOVER_GAP;
    const maxTop = Math.max(
      VIEWPORT_PADDING,
      window.innerHeight - popoverRect.height - VIEWPORT_PADDING
    );
    const maxLeft = Math.max(
      VIEWPORT_PADDING,
      window.innerWidth - popoverRect.width - VIEWPORT_PADDING
    );

    setPlacement(nextPlacement);
    setPopoverStyle({
      top: Math.min(Math.max(idealTop, VIEWPORT_PADDING), maxTop),
      left: Math.min(Math.max(anchorRect.left, VIEWPORT_PADDING), maxLeft),
    });
  }, [anchorRef, position]);

  useLayoutEffect(() => {
    updatePosition();

    const popover = popoverRef.current;
    if (!popover) return undefined;

    const resizeObserver = new ResizeObserver(updatePosition);
    resizeObserver.observe(popover);

    return () => resizeObserver.disconnect();
  }, [updatePosition]);

  useEffect(() => {
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [updatePosition]);

  useEffect(() => {
    function handleClickOutside(event) {
      const popover = popoverRef.current;
      const anchor =
        anchorRef?.current ?? markerRef.current?.previousElementSibling;
      const clickedPopover = popover?.contains(event.target);
      const clickedAnchor = anchor?.contains(event.target);

      if (popover && !clickedPopover && !clickedAnchor) {
        onClose();
      }
    }

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [anchorRef, onClose]);

  const popover = (
    <div
      ref={popoverRef}
      className={`popover popover--${placement} ${className ?? ''}`}
      style={{
        ...style,
        ...popoverStyle,
        visibility: popoverStyle ? style?.visibility : 'hidden',
      }}
      {...props}
    >
      {children}
      <div className="popover__content">{content}</div>
    </div>
  );

  return (
    <>
      <span ref={markerRef} className="popover__anchor-marker" aria-hidden />
      {createPortal(popover, document.body)}
    </>
  );
}
